import { useStore } from "zustand";
import { IExchange, marketStore } from "entities/market";
import { useEffect, useState } from "react";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import {
  ExchangeInfo,
  ExchangeStatus,
  ExchangeTimeFrame,
  ExchangeTimeFrameItem,
} from "./useExchangeTimeFrame";

const useExchangesTimeFrames = () => {
  const { t } = useTranslation("app");
  const { exchanges, setExchangesTimeFrames } = useStore(marketStore);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>("");
  const [tick, setTick] = useState<number>(0);

  const formatPercent = (value: number) => {
    return +value.toFixed(2);
  };

  const formatTimeLeft = (v: number) => {
    const hours = parseInt(String(v / 60));
    const days = parseInt(String(hours / 24));

    const getHours = (h: number) => {
      return `${h} ${h === 1 ? t("hour") : t("hours")}`;
    };

    const getDays = (d: number) => {
      return `${d} ${d === 1 ? t("day") : t("days")}`;
    };

    const getMinutes = (m: number) => {
      return `${m} ${m === 1 ? t("minute") : t("minutes")}`;
    };

    if (days > 0) {
      const leftHours = hours % 24;
      return `${getDays(days)}${
        leftHours > 0 ? ` ${t("and")} ${getHours(leftHours)}` : ""
      }`;
    }

    if (hours > 0) {
      const leftMinutes = v % 60;
      return `${getHours(hours)}${
        leftMinutes > 0 ? ` ${t("and")} ${getMinutes(leftMinutes)}` : ""
      }`;
    }

    if (v > 0) return getMinutes(v);

    return `${t("lessThen")} ${t("minute")}`;
  };

  const getTimeFrame = (exchange: IExchange | null) => {
    try {
      if (!exchange) return null;

      if (!exchange.open || !exchange.close || !exchange.timezone) {
        return null;
      }

      const {
        open,
        close,
        timezone,
        pre_open,
        pre_close,
        after_open,
        after_close,
      } = exchange;

      const startTime = moment(open, "HH:mm").tz(timezone, true);
      const endTime = moment(close, "HH:mm").tz(timezone, true);
      const preOpen = moment(pre_open, "HH:mm").tz(timezone, true);
      const preClose = moment(pre_close, "HH:mm").tz(timezone, true);
      const afterOpen = moment(after_open, "HH:mm").tz(timezone, true);
      const afterClose = moment(after_close, "HH:mm").tz(timezone, true);

      const nowTime = moment().tz(timezone).set("date", startTime.get("date"));
      const unit = 100 / (24 * 60);
      const zeroTime = moment("00:00", "HH:mm").tz(timezone, true);

      const point = formatPercent(nowTime.diff(zeroTime, "minutes") * unit);

      const weekday = nowTime.format("dddd");
      const isWeekend = ["Sunday", "Saturday"].includes(weekday);
      let info: ExchangeInfo | null = null;
      let time_left = 0;

      if (exchange?.isHoliday) {
        info =
          pre_open && pre_close
            ? ExchangeInfo.closeToPre
            : ExchangeInfo.closeToOpen;
        time_left = moment(pre_open && pre_close ? preOpen : startTime)
          .add(
            weekday === "Sunday" ? 1 : weekday === "Friday" ? 3 : 2,
            weekday === "Sunday" ? "day" : "days"
          )
          .diff(nowTime, "minutes");

        return {
          status: ExchangeStatus.Holiday,
          point,
          time_frame: [
            {
              from: "00:00",
              to: "24:00",
              type: ExchangeStatus.Holiday,
              progress_percent: 100,
            },
          ],
          exchange_code: exchange.symbol,
          exchange_logo: exchange.logo,
          exchange_name: exchange.name,
          timezone,
          current_index: 0,
          info,
          time_left: formatTimeLeft(time_left),
          isActive: false,
        };
      }

      if (isWeekend) {
        info =
          pre_open && pre_close
            ? ExchangeInfo.closeToPre
            : ExchangeInfo.closeToOpen;
        time_left = moment(pre_open && pre_close ? preOpen : startTime)
          .add(
            weekday === "Sunday" ? 1 : 2,
            weekday === "Sunday" ? "day" : "days"
          )
          .diff(nowTime, "minutes");

        return {
          status: ExchangeStatus.Weekend,
          point,
          time_frame: [
            {
              from: "00:00",
              to: "24:00",
              type: ExchangeStatus.Weekend,
              progress_percent: 100,
            },
          ],
          exchange_code: exchange.symbol,
          exchange_logo: exchange.logo,
          exchange_name: exchange.name,
          timezone,
          current_index: 0,
          info,
          time_left: formatTimeLeft(time_left),
          isActive: false,
        };
      }

      let status: ExchangeStatus = ExchangeStatus.Closed;
      if (nowTime.isBetween(startTime, endTime)) {
        status = ExchangeStatus.Open;
        info = ExchangeInfo.openToClose;
        time_left = endTime.diff(nowTime, "minutes");
      } else if (
        after_open &&
        after_close &&
        nowTime.isBetween(afterOpen, afterClose)
      ) {
        status = ExchangeStatus.PostMarket;
        info = ExchangeInfo.postToClose;
        time_left = afterClose.diff(nowTime, "minutes");
      } else if (
        pre_open &&
        pre_close &&
        nowTime.isBetween(preOpen, preClose)
      ) {
        status = ExchangeStatus.PreMarket;
        info = ExchangeInfo.preToOpenClose2;
        time_left = preClose.diff(nowTime, "minutes");
      }

      if (status === ExchangeStatus.Closed) {
        if (pre_open && pre_close && preOpen.diff(nowTime, "minutes") > 0) {
          info = ExchangeInfo.closeToPre;
          time_left = preOpen.diff(nowTime, "minutes");
        } else if (startTime.diff(nowTime, "minutes") > 0) {
          info =
            pre_open && pre_close
              ? ExchangeInfo.close2ToOpen
              : ExchangeInfo.closeToOpen;
          time_left = startTime.diff(nowTime, "minutes");
        } else if (
          after_open &&
          after_close &&
          afterOpen.diff(nowTime, "minutes") > 0
        ) {
          info = ExchangeInfo.close3ToPost;
          time_left = afterOpen.diff(nowTime, "minutes");
        }
      }

      const time_frame: ExchangeTimeFrameItem[] = [];

      if (pre_open && pre_close) {
        const preOpen = moment(pre_open, "HH:mm").tz(timezone, true);
        const preClose = moment(pre_close, "HH:mm").tz(timezone, true);

        time_frame.push({
          from: "00:00",
          to: pre_open,
          type: ExchangeStatus.Closed,
          progress_percent: formatPercent(
            preOpen.diff(zeroTime, "minutes") * unit
          ),
        });

        time_frame.push({
          from: pre_open,
          to: pre_close,
          type: ExchangeStatus.PreMarket,
          progress_percent: formatPercent(
            preClose.diff(preOpen, "minutes") * unit
          ),
        });
      } else {
        time_frame.push({
          from: "00:00",
          to: open,
          type: ExchangeStatus.Closed,
          progress_percent: formatPercent(
            startTime.diff(zeroTime, "minutes") * unit
          ),
        });
      }

      let lastItem = time_frame[time_frame.length - 1];
      let lastItemTime = moment(lastItem.to, "HH:mm").tz(timezone, true);
      const diffUntilOpen = startTime.diff(lastItemTime, "minutes");

      if (diffUntilOpen > 0) {
        time_frame.push({
          from: lastItem.to,
          to: open,
          type: ExchangeStatus.Closed,
          progress_percent: formatPercent(diffUntilOpen * unit),
        });
      }

      time_frame.push({
        from: open,
        to: close,
        type: ExchangeStatus.Open,
        progress_percent: formatPercent(
          endTime.diff(startTime, "minutes") * unit
        ),
      });

      zeroTime.add(1, "day");
      if (after_open && after_close) {
        const afterOpen = moment(after_open, "HH:mm").tz(timezone, true);
        const afterClose = moment(after_close, "HH:mm").tz(timezone, true);

        lastItem = time_frame[time_frame.length - 1];
        lastItemTime = moment(lastItem.to, "HH:mm").tz(timezone, true);
        const diffBetweenCloseAndAfterOpen = afterOpen.diff(
          lastItemTime,
          "minutes"
        );

        if (diffBetweenCloseAndAfterOpen > 0) {
          time_frame.push({
            from: lastItem.to,
            to: after_open,
            type: ExchangeStatus.Closed,
            progress_percent: formatPercent(
              diffBetweenCloseAndAfterOpen * unit
            ),
          });
        }

        time_frame.push({
          from: after_open,
          to: after_close,
          type: ExchangeStatus.PostMarket,
          progress_percent: formatPercent(
            afterClose.diff(afterOpen, "minutes") * unit
          ),
        });

        time_frame.push({
          from: after_close,
          to: "00:00",
          type: ExchangeStatus.Closed,
          progress_percent: formatPercent(
            zeroTime.diff(afterClose, "minutes") * unit
          ),
        });
      } else {
        time_frame.push({
          from: close,
          to: "00:00",
          type: ExchangeStatus.Closed,
          progress_percent: formatPercent(
            zeroTime.diff(endTime, "minutes") * unit
          ),
        });
      }

      if (status === ExchangeStatus.Closed && info === null) {
        info =
          pre_open && pre_close
            ? ExchangeInfo.closeToPre
            : ExchangeInfo.closeToOpen;

        if (weekday === "Friday") {
          if (pre_open && pre_close) {
            time_left = moment(preOpen).add(3, "days").diff(nowTime, "minutes");
          } else {
            time_left = moment(startTime)
              .add(3, "days")
              .diff(nowTime, "minutes");
          }
        } else {
          if (pre_open && pre_close) {
            time_left = moment(preOpen).add(1, "day").diff(nowTime, "minutes");
          } else {
            time_left = moment(startTime)
              .add(1, "day")
              .diff(nowTime, "minutes");
          }
        }
      }

      let currentPoint = point;
      let currentPercent = 0;
      const timeFrameIndexByPoint = time_frame.findIndex((v, i) => {
        currentPercent += time_frame[i].progress_percent;
        return point <= currentPercent;
      });

      try {
        const item = time_frame[timeFrameIndexByPoint];
        currentPoint = formatPercent(
          nowTime.diff(
            moment(item.from, "HH:mm").tz(timezone, true),
            "minutes"
          ) *
            (100 /
              moment(item.to, "HH:mm").diff(
                moment(item.from, "HH:mm"),
                "minutes"
              ))
        );
      } catch (error) {}

      return {
        status,
        point: currentPoint,
        time_frame,
        exchange_code: exchange.symbol,
        exchange_logo: exchange.logo,
        exchange_name: exchange.name,
        timezone,
        current_index: timeFrameIndexByPoint,
        info: info as ExchangeInfo,
        time_left: formatTimeLeft(time_left),
        isActive: [
          ExchangeStatus.Open,
          ExchangeStatus.PreMarket,
          ExchangeStatus.PostMarket,
        ].includes(status),
      };
    } catch (error) {
      return null;
    }
  };

  const getTimeFrameV2 = (exchange: IExchange | null) => {
    try {
      if (!exchange) return null;

      if (!exchange.open || !exchange.close || !exchange.timezone) {
        return null;
      }

      const {
        open,
        close,
        timezone,
        pre_open,
        pre_close,
        after_open,
        after_close,
        isHoliday,
      } = exchange;

      const MINUTES_IN_DAY = 24 * 60;
      const unit = 100 / MINUTES_IN_DAY;
      const nowTime = moment().tz(timezone);
      const dayStart = nowTime.clone().startOf("day");
      const dayEnd = dayStart.clone().add(1, "day");
      const dayOfWeek = nowTime.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday

      const parseTime = (value?: string | null) => {
        if (!value) return null;
        const [hours, minutes] = value.split(":").map((v) => Number(v));
        if (Number.isNaN(hours) || Number.isNaN(minutes)) {
          return null;
        }
        return dayStart
          .clone()
          .hour(hours)
          .minute(minutes)
          .second(0)
          .millisecond(0);
      };

      const startTime = parseTime(open);
      const endTime = parseTime(close);

      if (!startTime || !endTime) {
        return null;
      }

      const hasPreMarket = Boolean(pre_open && pre_close);
      const hasPostMarket = Boolean(after_open && after_close);

      const preOpen = hasPreMarket ? parseTime(pre_open) : null;
      const preClose = hasPreMarket ? parseTime(pre_close) : null;
      const postOpen = hasPostMarket ? parseTime(after_open) : null;
      const postClose = hasPostMarket ? parseTime(after_close) : null;

      const clampMinutes = (value: number) => Math.max(value, 0);

      const buildFullDayFrame = (
        status: ExchangeStatus,
        info: ExchangeInfo,
        timeLeftMinutes: number
      ) => {
        const point = formatPercent(
          Math.min(
            Math.max(nowTime.diff(dayStart, "minutes"), 0),
            MINUTES_IN_DAY
          ) * unit
        );

        return {
          status,
          point,
          time_frame: [
            {
              from: "00:00",
              to: "24:00",
              type: status,
              progress_percent: 100,
            },
          ],
          exchange_code: exchange.symbol,
          exchange_logo: exchange.logo,
          exchange_name: exchange.name,
          timezone,
          current_index: 0,
          info,
          time_left: formatTimeLeft(clampMinutes(timeLeftMinutes)),
          isActive: false,
        };
      };

      if (isHoliday) {
        const baseMoment =
          (hasPreMarket ? preOpen : startTime)?.clone() || startTime.clone();
        const addDays = dayOfWeek === 0 ? 1 : dayOfWeek === 5 ? 3 : 2; // 0 = Sunday, 5 = Friday
        const nextMoment = baseMoment.add(addDays, "days");
        const info = hasPreMarket
          ? ExchangeInfo.closeToPre
          : ExchangeInfo.closeToOpen;

        return buildFullDayFrame(
          ExchangeStatus.Holiday,
          info,
          nextMoment.diff(nowTime, "minutes")
        );
      }

      if (isWeekend) {
        const baseMoment =
          (hasPreMarket ? preOpen : startTime)?.clone() || startTime.clone();
        const addDays = dayOfWeek === 0 ? 1 : 2; // 0 = Sunday, 6 = Saturday
        const nextMoment = baseMoment.add(
          addDays,
          dayOfWeek === 0 ? "day" : "days"
        );
        const info = hasPreMarket
          ? ExchangeInfo.closeToPre
          : ExchangeInfo.closeToOpen;

        return buildFullDayFrame(
          ExchangeStatus.Weekend,
          info,
          nextMoment.diff(nowTime, "minutes")
        );
      }

      type TimelineSegment = ExchangeTimeFrameItem & {
        fromMoment: moment.Moment;
        toMoment: moment.Moment;
      };

      const timeline: TimelineSegment[] = [];

      const pushSegment = (
        fromMoment: moment.Moment,
        toMoment: moment.Moment,
        type: ExchangeStatus,
        fromLabel?: string,
        toLabel?: string
      ) => {
        if (!toMoment || !fromMoment) return;
        const diff = toMoment.diff(fromMoment, "minutes");
        if (diff <= 0) return;

        timeline.push({
          from: fromLabel ?? fromMoment.format("HH:mm"),
          to: toLabel ?? toMoment.format("HH:mm"),
          type,
          progress_percent: formatPercent(diff * unit),
          fromMoment: fromMoment.clone(),
          toMoment: toMoment.clone(),
        });
      };

      if (hasPreMarket && preOpen && preClose) {
        pushSegment(
          dayStart,
          preOpen,
          ExchangeStatus.Closed,
          "00:00",
          pre_open as string
        );
        pushSegment(
          preOpen,
          preClose,
          ExchangeStatus.PreMarket,
          pre_open as string,
          pre_close as string
        );
        pushSegment(
          preClose,
          startTime,
          ExchangeStatus.Closed,
          pre_close as string,
          open
        );
      } else {
        pushSegment(dayStart, startTime, ExchangeStatus.Closed, "00:00", open);
      }

      pushSegment(startTime, endTime, ExchangeStatus.Open, open, close);

      if (hasPostMarket && postOpen && postClose) {
        pushSegment(
          endTime,
          postOpen,
          ExchangeStatus.Closed,
          close,
          after_open as string
        );
        pushSegment(
          postOpen,
          postClose,
          ExchangeStatus.PostMarket,
          after_open as string,
          after_close as string
        );
        pushSegment(
          postClose,
          dayEnd,
          ExchangeStatus.Closed,
          after_close as string,
          "00:00"
        );
      } else {
        pushSegment(endTime, dayEnd, ExchangeStatus.Closed, close, "00:00");
      }

      let status: ExchangeStatus = ExchangeStatus.Closed;
      let info: ExchangeInfo | null = null;
      let timeLeftMinutes = 0;

      const inRange = (
        start: moment.Moment | null,
        end: moment.Moment | null
      ) => {
        if (!start || !end) return false;
        return nowTime.isSameOrAfter(start) && nowTime.isBefore(end);
      };

      if (hasPreMarket && preOpen && preClose && inRange(preOpen, preClose)) {
        status = ExchangeStatus.PreMarket;
        info = ExchangeInfo.preToOpenClose2;
        timeLeftMinutes = preClose.diff(nowTime, "minutes");
      } else if (inRange(startTime, endTime)) {
        status = ExchangeStatus.Open;
        info = ExchangeInfo.openToClose;
        timeLeftMinutes = endTime.diff(nowTime, "minutes");
      } else if (
        hasPostMarket &&
        postOpen &&
        postClose &&
        inRange(postOpen, postClose)
      ) {
        status = ExchangeStatus.PostMarket;
        info = ExchangeInfo.postToClose;
        timeLeftMinutes = postClose.diff(nowTime, "minutes");
      } else {
        const upcoming: Array<{ target: moment.Moment; info: ExchangeInfo }> =
          [];

        if (hasPreMarket && preOpen && preOpen.isAfter(nowTime)) {
          upcoming.push({ target: preOpen, info: ExchangeInfo.closeToPre });
        }

        if (startTime.isAfter(nowTime)) {
          upcoming.push({
            target: startTime,
            info: hasPreMarket
              ? ExchangeInfo.close2ToOpen
              : ExchangeInfo.closeToOpen,
          });
        }

        if (hasPostMarket && postOpen && postOpen.isAfter(nowTime)) {
          upcoming.push({
            target: postOpen,
            info: ExchangeInfo.close3ToPost,
          });
        }

        if (upcoming.length) {
          const next = upcoming.sort((a, b) => a.target.diff(b.target))[0];
          info = next.info;
          timeLeftMinutes = next.target.diff(nowTime, "minutes");
        }
      }

      if (status === ExchangeStatus.Closed && info === null) {
        const baseMoment = (
          hasPreMarket && preOpen ? preOpen : startTime
        ).clone();
        const addDays = dayOfWeek === 5 ? 3 : 1; // 5 = Friday
        const nextMoment = baseMoment.add(addDays, "day");
        info = hasPreMarket
          ? ExchangeInfo.closeToPre
          : ExchangeInfo.closeToOpen;
        timeLeftMinutes = nextMoment.diff(nowTime, "minutes");
      }

      const minutesFromStart = Math.min(
        Math.max(nowTime.diff(dayStart, "minutes"), 0),
        MINUTES_IN_DAY
      );
      const dayPoint = formatPercent(minutesFromStart * unit);

      let currentIndex = 0;
      let accumulated = 0;
      for (let i = 0; i < timeline.length; i += 1) {
        accumulated += timeline[i].progress_percent;
        if (dayPoint <= accumulated) {
          currentIndex = i;
          break;
        }
        if (i === timeline.length - 1) {
          currentIndex = i;
        }
      }

      let currentPoint = dayPoint;
      const currentSegment = timeline[currentIndex];
      if (currentSegment) {
        const segmentDuration = currentSegment.toMoment.diff(
          currentSegment.fromMoment,
          "minutes"
        );
        if (segmentDuration > 0) {
          const elapsed = nowTime.diff(currentSegment.fromMoment, "minutes");
          const ratio = Math.min(Math.max(elapsed / segmentDuration, 0), 1);
          currentPoint = formatPercent(ratio * 100);
        }
      }

      return {
        status,
        point: currentPoint,
        time_frame: timeline.map(({ from, to, type, progress_percent }) => ({
          from,
          to,
          type,
          progress_percent,
        })),
        exchange_code: exchange.symbol,
        exchange_logo: exchange.logo,
        exchange_name: exchange.name,
        timezone,
        current_index: currentIndex,
        info: (info ?? ExchangeInfo.closeToOpen) as ExchangeInfo,
        time_left: formatTimeLeft(clampMinutes(timeLeftMinutes)),
        isActive: [
          ExchangeStatus.Open,
          ExchangeStatus.PreMarket,
          ExchangeStatus.PostMarket,
        ].includes(status),
      };
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 5 * 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (exchanges.length) {
      const now = moment().format("HH:mm");
      if (now !== lastUpdateTime) {
        setLastUpdateTime(now);

        const map = new Map<string, ExchangeTimeFrame>();
        exchanges.map((exchange) => {
          // map.set(exchange.symbol, getTimeFrame(exchange));
          map.set(exchange.symbol, getTimeFrameV2(exchange));
        });

        setExchangesTimeFrames(map);
      }
    } else {
      setExchangesTimeFrames(null);
    }
  }, [exchanges, lastUpdateTime, tick]);

  return {};
};

export default useExchangesTimeFrames;
